import { useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import Layout from '../Layout';
import {
  Flex,
  Box,
  Logo,
  H1,
  Link,
  ButtonAnchor,
  Loading
} from '../../components';
import { useRouter } from 'next/router';
import { getQueryString } from '../../lib/utils';
import { useLazyQuery } from '@apollo/client';
import { orderQuery, orderQueryVariables } from '../../core-types';
import { ORDER_QUERY } from '../Receipt/graphql';
import { useUserShouldOnboard } from '../Onboarding/useOnboardingSteps';
import { CurrentSubscription } from '../Profile/CurrentSubscription';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { LaunchAppButton } from '../../components/LaunchApp';

export const messages = defineMessages({
  title: {
    id: 'pages.onboarding.title',
    defaultMessage: 'Onboarding'
  },
  heading: {
    id: 'PreOnboarding.heading',
    defaultMessage: "Success! {br}Let's ride"
  },
  completeProfile: {
    id: 'PreOnboarding.completeProfile',
    defaultMessage: 'Complete your profile'
  },
  notNow: {
    id: 'seeHowItWorks',
    defaultMessage: 'See how it works'
  }
});

export const PostCheckout = () => {
  const router = useRouter();
  const intl = useIntl();
  const token = getQueryString(router.query.token);
  const { currentUser, loading: currentUserLoading } = useCurrentUser({
    fetchPolicy: 'network-only'
  });
  const [getOrder] = useLazyQuery<orderQuery, orderQueryVariables>(
    ORDER_QUERY,
    {
      ssr: false
    }
  );

  useEffect(() => {
    if (!token) {
      return;
    }
    getOrder({ variables: { token } });
  }, []);

  const userMustOnboardRes = useUserShouldOnboard(currentUser, true);
  const userCanOnboardRes = useUserShouldOnboard(currentUser, false);
  const userMustOnboard =
    !userMustOnboardRes.loading && userMustOnboardRes.userRequiresOnboarding;
  const userCanOnboard =
    !userCanOnboardRes.loading && userCanOnboardRes.userRequiresOnboarding;

  return (
    <Layout
      title={intl.formatMessage(messages.title)}
      url={router}
      loading={false}
      hideChrome
      requiresAuth={false}
      hideIntercomLauncher
      contentProps={{
        // Override Content defaultProps
        py: 0,
        mx: 0,
        mb: 0
      }}
      render={() => {
        return (
          <Flex
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            minHeight="100vh"
            textAlign="center"
          >
            <Box my="md" data-testid="PreOnboarding">
              <Logo color="primary" />
            </Box>
            <Box>
              <H1>
                <FormattedMessage
                  {...messages.heading}
                  values={{ br: <br /> }}
                />
              </H1>

              <Box mx="auto" width="max-content">
                {currentUserLoading && <Loading mb="sm" />}
                <CurrentSubscription />
              </Box>
              {userCanOnboard && (
                <Box mb="xs">
                  <Link
                    route="onboarding"
                    params={{ from: 'post-checkout' }}
                    passHref
                  >
                    <ButtonAnchor
                      shape="cool"
                      variant="primary"
                      data-testid="Complete_Onboarding"
                    >
                      <FormattedMessage {...messages.completeProfile} />
                    </ButtonAnchor>
                  </Link>
                </Box>
              )}
              <Box mb="xs">
                <LaunchAppButton />
              </Box>
              {!userMustOnboard && (
                <Link
                  route="how-it-works"
                  params={{ from: 'onboarding' }}
                  passHref
                >
                  <ButtonAnchor
                    shape={userCanOnboard ? undefined : 'cool'}
                    variant={userCanOnboard ? 'link' : 'primary'}
                    data-testid="PreOnboarding__NotNow"
                  >
                    <FormattedMessage {...messages.notNow} />
                  </ButtonAnchor>
                </Link>
              )}
            </Box>
          </Flex>
        );
      }}
    />
  );
};
